import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import {
  useGenerate,
  useGenerationStatus,
  useDownload,
  useExtendVideo,
  useSaveAsCharacter,
  useCharacters,
  type ExtendVideoRequest,
} from "@/hooks";
import { Toast, UpgradePlansModal, AgeVerificationModal } from "@/components/ui";
import { useAgeVerification } from "@/components/ui/gating";
import { useUserStore } from "@/stores/user-store";
import { hasAccessToPlan } from "@/config/navigation";
import {
  trackPageView,
  trackVideoGenerated,
  trackImageGenerated,
} from "@/services/analytics";
import type { ModelId, GenerateRequest } from "@/types";
import {
  calculateProgress,
  calculateTimeRemaining,
} from "@/lib/generation-utils";
import {
  GenerationBootingState,
  GenerationErrorState,
  GenerationFailedState,
  GenerationSuccessState,
  GenerationLoadingState,
} from "@/components/features/generation";

interface LocationState {
  modelId?: ModelId;
  prompt?: string;
  type?: "image" | "video";
  generateParams?: Omit<GenerateRequest, "user_id">;
  extendVideoParams?: ExtendVideoRequest;
  /**
   * Marks an unsaved character draft. The success page renders Save /
   * Edit / Try Again / Discard instead of the default Edit / Animate
   * row, and the flag is propagated through Edit hops so iteration
   * stays a draft until the user explicitly Saves.
   */
  isCharacterDraft?: boolean;
}

export function GenerationPage() {
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const { isSignedIn, isLoaded: isUserLoaded, user } = useUser();
  const { plan } = useUserStore();
  const hasCreatorAccess = isSignedIn && hasAccessToPlan(plan, "creator");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { showModal: showAgeModal, handleConfirm: ageConfirm, handleDecline: ageDecline, requireVerification } = useAgeVerification();

  const isExtendVideo = !!state?.extendVideoParams;
  const hasParams = !!(state?.generateParams || state?.extendVideoParams);

  // Track page view
  useEffect(() => {
    if (urlId && urlId !== "new") {
      trackPageView("Generation", "Generation", user?.id, {
        generation_id: urlId,
        generation_type: state?.type,
        model_id: state?.modelId,
      });
    }
  }, [urlId, user?.id, state?.type, state?.modelId]);

  // Clear browser history state (but keep React Router state for generation start)
  // This prevents refresh from restarting generation, but allows initial generation to proceed
  useEffect(() => {
    if (urlId === "new" && hasParams) {
      // Only clear browser history, don't navigate (which would clear React Router state)
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Redirect if accessing /generation/new without params or not signed in
  useEffect(() => {
    if (!isUserLoaded) return;
    if (urlId === "new" && (!hasParams || !isSignedIn)) {
      navigate("/", { replace: true });
    }
  }, [urlId, hasParams, navigate, isUserLoaded, isSignedIn]);

  const isNewGeneration = urlId === "new" || (!urlId && hasParams);
  const [generationId, setGenerationId] = useState<string | null>(
    urlId && urlId !== "new" ? urlId : null
  );
  const [isBooting, setIsBooting] = useState(isNewGeneration);
  const [bootError, setBootError] = useState<string | null>(null);
  const hasStartedGeneration = useRef(false);

  const generateMutation = useGenerate();
  const extendVideoMutation = useExtendVideo();
  const { data, error } = useGenerationStatus(isBooting ? null : generationId);
  const generation = data?.generation;

  // Draft state. Server-persisted via `params.is_character_draft`
  // (set inside `POST /generate` when the create page sends the
  // flag), so it survives a hard refresh of `/generation/:id`. We
  // fall back to the nav-state hint for the brief window before the
  // row's first poll lands.
  const persistedDraft = !!(generation?.params as Record<string, unknown> | null)?.is_character_draft;
  const isCharacterDraft = persistedDraft || !!state?.isCharacterDraft;

  // Save-as-Character: the server owns naming logic (custom name +
  // collision check, or auto-pick "Character N" if blank). We only
  // fetch the existing character list to pre-fill the modal with the
  // next "Character N" as a friendly suggestion the user can edit.
  const saveAsCharacter = useSaveAsCharacter();
  const { data: charactersData } = useCharacters();
  const suggestedCharacterName = useMemo(() => {
    const list = charactersData?.characters ?? [];
    let max = 0;
    for (const c of list) {
      const m = c.name.match(/^Character (\d+)$/);
      if (m) {
        const num = parseInt(m[1], 10);
        if (!Number.isNaN(num) && num > max) max = num;
      }
    }
    return `Character ${max + 1}`;
  }, [charactersData]);

  const handleSaveCharacter = async (name: string) => {
    if (!generation || !generationId) return;
    if (saveAsCharacter.isPending) return;
    try {
      const result = await saveAsCharacter.mutateAsync({
        generationId,
        name,
      });
      navigate(`/characters/${encodeURIComponent(result.name)}`);
    } catch {
      // The mutation's error state surfaces back to the modal via
      // `saveError` below — no need to re-throw or toast here.
    }
  };

  const saveError = saveAsCharacter.error
    ? (saveAsCharacter.error as { errorMessage?: string; message?: string })
        .errorMessage ??
      (saveAsCharacter.error as Error).message ??
      "Failed to save character"
    : null;

  const [tick, setTick] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);

  // Kick off the generation exactly once per mount. The page
  // remounts on every navigation to `/generation/...` (see
  // `GenerationRoute` in App.tsx), so this effect always represents
  // a fresh user submission. No fingerprint/sessionStorage dance —
  // the form's own button-disable + the React Strict Mode guard
  // (`hasStartedGeneration` ref) cover the only race we care about.
  useEffect(() => {
    if (!isUserLoaded || !isSignedIn) return;
    if (!hasParams || hasStartedGeneration.current || generationId) return;

    const params = state?.generateParams || state?.extendVideoParams;
    if (!params) return;

    hasStartedGeneration.current = true;

    const startGeneration = async () => {
      try {
        // Age verification for NSFW prompts (one-time, stored in localStorage)
        const promptToCheck = state?.generateParams?.prompt || state?.extendVideoParams?.prompt;
        const ageOk = await requireVerification(promptToCheck);
        if (!ageOk) {
          hasStartedGeneration.current = false;
          navigate(-1);
          return;
        }

        // Use the appropriate mutation based on generation type
        const result = isExtendVideo
          ? await extendVideoMutation.mutateAsync(state!.extendVideoParams!)
          : await generateMutation.mutateAsync(state!.generateParams!);

        window.history.replaceState(
          {},
          "",
          `/generation/${result.generation.id}`
        );
        setGenerationId(result.generation.id);

        const createdAt = new Date(result.generation.created_at).getTime();
        const now = Date.now();
        const timeDiff = now - createdAt;
        const validCreatedAt = timeDiff >= 0 && timeDiff < 30000;
        setStartTime(validCreatedAt ? createdAt : now);
        setIsBooting(false);
      } catch (err: any) {
        setBootError(
          err.errorMessage || err.message || "Failed to start generation"
        );
        setIsBooting(false);
      }
    };

    startGeneration();
  }, [isUserLoaded, isSignedIn]);

  // Set start time from generation's created_at on refresh
  useEffect(() => {
    if (generation?.created_at && startTime === null) {
      setStartTime(new Date(generation.created_at).getTime());
    }
  }, [generation?.created_at, startTime]);

  // Tick timer for progress updates
  useEffect(() => {
    if (isBooting || startTime === null) return;
    if (generation?.status === "completed" || generation?.status === "failed")
      return;

    const interval = setInterval(() => setTick((prev) => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [isBooting, startTime, generation?.status]);

  const timeRemaining = useMemo(
    () =>
      calculateTimeRemaining(startTime, state?.modelId || generation?.model_id),
    [state?.modelId, generation?.model_id, startTime, tick]
  );

  const progress = useMemo(
    () => calculateProgress(startTime, state?.modelId || generation?.model_id),
    [state?.modelId, generation?.model_id, startTime, tick]
  );

  const isComplete = generation?.status === "completed";
  const isFailed = generation?.status === "failed";
  const hasTrackedCompletionRef = useRef(false);

  // Track generation completion
  useEffect(() => {
    if (isComplete && generation && !hasTrackedCompletionRef.current) {
      hasTrackedCompletionRef.current = true;
      const modelId = state?.modelId || generation.model_id || "unknown";
      const promptLength = generation.prompt?.length || 0;

      if (generation.media_type === "VIDEO") {
        trackVideoGenerated({
          user_id: user?.id,
          model: modelId,
          prompt_length: promptLength,
          video_id: generation.id,
          generation_type: isExtendVideo ? "extend" : "generate",
        });
      } else {
        trackImageGenerated({
          user_id: user?.id,
          model: modelId,
          prompt_length: promptLength,
          image_id: generation.id,
          generation_type: "generate",
        });
      }
    }
  }, [isComplete, generation, user?.id, state?.modelId, isExtendVideo]);

  // Download hook
  const {
    download,
    showToast: showDownloadToast,
    setShowToast: setShowDownloadToast,
    progress: downloadProgress,
    isComplete: downloadComplete,
    isError: downloadError,
    assetType: downloadAssetType,
  } = useDownload();

  const handleDownload = () => {
    if (!generation?.url) return;
    const downloadUrl = generation.url_watermarked || generation.url;
    const assetType = generation.media_type === "VIDEO" ? "video" : "image";
    const extension = assetType === "video" ? "mp4" : "png";
    const filename = `generation_${generationId?.slice(0, 8)}.${extension}`;
    download({ url: downloadUrl, filename, assetType });
  };

  // Error states
  if (bootError) {
    return (
      <GenerationErrorState
        title="Failed to Start Generation"
        message={bootError}
        actionLabel="Go Back"
        onAction={() => navigate(-1)}
      />
    );
  }

  if (isBooting) {
    return (
      <>
        <GenerationBootingState />
        {showAgeModal && <AgeVerificationModal onConfirm={ageConfirm} onDecline={ageDecline} />}
      </>
    );
  }

  if (error || (!generationId && !hasParams)) {
    return (
      <GenerationErrorState
        title="Generation Not Found"
        message="This generation doesn't exist or has expired."
        actionLabel="Create New Generation"
        onAction={() => navigate("/video/i2v")}
      />
    );
  }

  // Success state
  if (isComplete && generation) {
    return (
      <>
        <Toast
          isOpen={showDownloadToast}
          onClose={() => setShowDownloadToast(false)}
          type={downloadError ? "error" : "loading"}
          message={
            downloadError
              ? "Download failed. Please try again later."
              : downloadComplete
              ? "Download complete!"
              : `Downloading ${downloadAssetType}...`
          }
          progress={downloadError ? undefined : downloadProgress}
          isComplete={downloadComplete}
          duration={downloadError ? 4000 : 2000}
        />
        <GenerationSuccessState
          generation={generation}
          generationId={generationId!}
          plan={plan}
          modelId={state?.modelId}
          hasCreatorAccess={hasCreatorAccess || false}
          onDownload={handleDownload}
          onViewAssets={() => navigate("/assets")}
          onEditImage={() =>
            navigate("/images/image-edit", {
              state: {
                imageUrl: generation.url,
                imageId: generation.id,
                folder: generation.folder ?? undefined,
                // Propagate draft mode so the next generation also
                // lands on the Save / Edit / Try Again UI.
                isCharacterDraft: isCharacterDraft || undefined,
              },
            })
          }
          onAnimateImage={() =>
            navigate("/video/i2v", {
              state: {
                imageUrl: generation.url,
                imageId: generation.id,
                folder: generation.folder ?? undefined,
              },
            })
          }
          onViewCharacter={(folder) =>
            navigate(`/characters/${encodeURIComponent(folder)}`)
          }
          onTweakAgain={(folder) =>
            // Keep tweaks inside the character section so the user
            // never bounces out to the generic image-edit page.
            // `fromId` selects this exact result as the source for
            // the next edit (rather than defaulting to canonical).
            navigate(
              `/characters/edit?name=${encodeURIComponent(folder)}&fromId=${generation.id}`,
            )
          }
          onAnimateCharacter={(folder) =>
            navigate("/video/i2v", {
              state: {
                imageUrl: generation.url,
                imageId: generation.id,
                folder,
              },
            })
          }
          // For drafts, "Try Again" loops back to the character prompt
          // page; otherwise the existing image-no-folder branch is used
          // for plain T2V suggestions and routes to /video/i2v.
          onGenerateAnother={() =>
            navigate(isCharacterDraft ? "/characters" : "/video/i2v")
          }
          isCharacterDraft={isCharacterDraft}
          onSaveCharacter={handleSaveCharacter}
          isSaving={saveAsCharacter.isPending}
          saveError={saveError}
          onClearSaveError={() => saveAsCharacter.reset()}
          suggestedCharacterName={suggestedCharacterName}
          // Inline edit on the character-draft success page: go to
          // /generation/new with `qwen_image_edit` + the current
          // generation as the source. Keeps the same idempotency,
          // age-gate, polling, and error-handling machinery as a
          // first-time generate, just with a chained edit prompt.
          onEditWithPrompt={(editPrompt) => {
            navigate("/generation/new", {
              state: {
                modelId: "qwen_image_edit",
                prompt: editPrompt,
                type: "image",
                generateParams: {
                  model_id: "qwen_image_edit",
                  prompt: editPrompt,
                  input_generation_id: generation.id,
                  is_character_draft: true,
                },
                isCharacterDraft: true,
              },
            });
          }}
          onExtendVideo={() => {
            if (hasCreatorAccess) {
              navigate("/extend-video", {
                state: {
                  video: {
                    id: generation.id,
                    url: generation.url,
                    media_type: generation.media_type,
                  },
                },
              });
            } else {
              setShowUpgradeModal(true);
            }
          }}
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />
        <UpgradePlansModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          preset="upgrade"
          presetContext={{ feature: "Extend Video" }}
          allowedPlans={["creator", "pro"]}
        />
      </>
    );
  }

  // Failed state
  if (isFailed) {
    const params = generation?.params as Record<string, any> | null;
    const wasRefunded = params?.refunded === true;
    const refundAmount = params?.refund_amount;
    const hasViewedRefund = generationId
      ? sessionStorage.getItem(`refund_ack_${generationId}`) === "true"
      : false;
    const showRefundBanner = wasRefunded && !hasViewedRefund;

    return (
      <GenerationFailedState
        errorMessage={generation?.error_message}
        generationId={generationId!}
        wasRefunded={wasRefunded}
        refundAmount={refundAmount}
        showRefundBanner={showRefundBanner}
        onTryAgain={() => navigate(-1)}
      />
    );
  }

  // Determine if user should see upgrade banner (free and starter plans only)
  const showUpgradeBanner = plan === "free" || plan === "starter";

  // Loading state
  return (
    <>
      <GenerationLoadingState
        type={state?.type}
        mediaType={generation?.media_type}
        progress={progress}
        timeRemaining={timeRemaining}
        prompt={state?.prompt || generation?.prompt}
        showUpgradeBanner={showUpgradeBanner}
        onUpgradeClick={() => setShowUpgradeModal(true)}
      />
      <UpgradePlansModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        preset="upgrade"
        title="Upgrade Your Experience"
        description="Get faster generation times, 3x quality improvement, and unlock premium features."
      />
    </>
  );
}
